import Check from '@pinecast/common/icons/Check';
import ErrorFlag from '@pinecast/common/icons/ErrorFlag';
import {FormattedMessage} from '@pinecast/i18n';
import {useColors, useCSS} from '@pinecast/styles';

import type {TranscriptSaveStatus} from './types';

// Floating pill that reports the transcript's aggregate save state. It sticks
// to the top of the viewport so it stays visible while scrolling a long
// transcript, and it is a live region so screen readers hear "Saving…" /
// "All changes saved" transitions without losing their place in the text.
const SaveStatusIndicator = ({status}: {status: TranscriptSaveStatus}) => {
  const css = useCSS();
  const designColors = useColors();
  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '8px',
        pointerEvents: 'none',
        position: 'sticky',
        top: '8px',
        zIndex: 2,
      })}
    >
      <div
        role="status"
        className={css({
          alignItems: 'center',
          backgroundColor: designColors.contentBackground,
          borderRadius: 16,
          boxShadow: `0 0 0 1px ${
            status === 'error'
              ? designColors.borderRedAccent
              : designColors.contentBorder
          }, 0 1px 4px rgba(0, 0, 0, 0.1)`,
          color:
            status === 'idle'
              ? designColors.contentTextAccent
              : designColors.contentText,
          display: 'flex',
          fontSize: 13,
          gap: '6px',
          lineHeight: '18px',
          minHeight: 28,
          padding: '4px 12px',
          pointerEvents: 'auto',
        })}
      >
        {status === 'saved' && (
          <Check color={designColors.contentText} size={14} />
        )}
        {status === 'error' && (
          <ErrorFlag color={designColors.borderRedAccent} size={14} />
        )}
        {status === 'idle' && (
          <FormattedMessage
            description="Hint in the transcript editor that edits are saved automatically"
            defaultMessage="Changes are saved automatically"
          />
        )}
        {status === 'saving' && (
          <FormattedMessage
            description="Status shown while transcript edits are being saved"
            defaultMessage="Saving…"
          />
        )}
        {status === 'saved' && (
          <FormattedMessage
            description="Status shown when all transcript edits have been saved"
            defaultMessage="All changes saved"
          />
        )}
        {status === 'error' && (
          <FormattedMessage
            description="Status shown when some transcript edits failed to save"
            defaultMessage="Some changes couldn't be saved"
          />
        )}
      </div>
    </div>
  );
};

export default SaveStatusIndicator;
